import React, { useEffect, useRef, useState } from 'react';
import { MEDIA_VARIANTS } from '../../../media';
import IconButton from '../../../ui/IconButton';
import { GameItemTooltip } from '../Game_ItemTooltip';
import { ImageLoader } from '../ImageLoader';
import './CombatRewardsPopup.css';

export const COMBAT_REWARD_DISMISS_MS = 5000;

function RewardLootItem({ actionLabel, entry, onOpen, onPin, title }) {
  const anchorRef = useRef(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  return (
    <>
      <button
        aria-label={actionLabel}
        onBlur={() => setTooltipVisible(false)}
        onClick={() => {
          setTooltipVisible(false);
          onPin?.();
          onOpen?.();
        }}
        onFocus={() => setTooltipVisible(true)}
        onMouseEnter={() => setTooltipVisible(true)}
        onMouseLeave={() => setTooltipVisible(false)}
        ref={anchorRef}
        title={title}
        type="button"
      >
        <ImageLoader
          animatedMediaId={
            entry.item?.animatedMediaId
            || entry.item?.animatedImageId
            || ''
          }
          className="combat-rewards-popup__item-image"
          imageId={entry.item?.imageId || ''}
          itemId={entry.itemId}
          staticPreview
          variant={MEDIA_VARIANTS.TILE}
        />
        <span>{entry.name}</span>
        <strong>×{entry.quantity}</strong>
      </button>
      {tooltipVisible ? (
        <GameItemTooltip
          anchorRect={anchorRef.current?.getBoundingClientRect()}
          getAnchorElement={() => anchorRef.current}
          getAnchorRect={() => anchorRef.current?.getBoundingClientRect()}
          itemId={entry.itemId}
          itemInstanceId={entry.itemInstanceId || ''}
        />
      ) : null}
    </>
  );
}

export default function CombatRewardsPopup({
  actionsOpen = false,
  onClose,
  onOpenGroundLoot,
  onOpenPack,
  onPin,
  pinned = false,
  summary,
}) {
  useEffect(() => {
    if (pinned) return undefined;
    const timer = window.setTimeout(onClose, COMBAT_REWARD_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [onClose, pinned, summary]);

  const pin = () => onPin?.();
  const loot = summary?.loot || [];
  const groundLoot = summary?.groundLoot || [];

  return (
    <aside
      aria-label="Fight rewards"
      className={`combat-rewards-popup${actionsOpen ? ' is-actions-open' : ''}`}
      data-floating-window-obstacle="true"
      onFocusCapture={pin}
      onMouseEnter={pin}
      onPointerDown={pin}
    >
      <header className="combat-rewards-popup__header">
        <span>
          <span
            aria-live="polite"
            className="combat-rewards-popup__eyebrow"
            role="status"
          >
            Fight won
          </span>
          <strong>{summary?.enemyName || 'Enemy defeated'}</strong>
        </span>
        <IconButton
          className="combat-rewards-popup__close"
          icon="close"
          label="Close fight rewards"
          onClick={onClose}
        />
      </header>

      <div className="combat-rewards-popup__xp">
        <span>Experience</span>
        <strong>+{summary?.xp || 0} XP</strong>
      </div>

      {loot.length > 0 ? (
        <ul className="combat-rewards-popup__loot" aria-label="Loot gained">
          {loot.map(entry => (
            <li key={entry.key}>
              <RewardLootItem
                actionLabel={`Open ${entry.name} in Pack`}
                entry={entry}
                onOpen={onOpenPack}
                onPin={pin}
                title="Open Pack to inspect"
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="combat-rewards-popup__empty">
          {groundLoot.length > 0
            ? 'No loot moved directly into your Pack.'
            : 'No loot dropped.'}
        </p>
      )}

      {groundLoot.length > 0 ? (
        <section
          aria-label="Loot waiting on the ground"
          className="combat-rewards-popup__ground"
        >
          <div className="combat-rewards-popup__ground-heading">
            <span>Ground remainder</span>
            <button
              onClick={() => {
                pin();
                onOpenGroundLoot?.();
              }}
              type="button"
            >
              Open treasure
            </button>
          </div>
          <ul className="combat-rewards-popup__loot">
            {groundLoot.map(entry => (
              <li key={entry.key}>
                <RewardLootItem
                  actionLabel={`Open ${entry.name} on the ground`}
                  entry={entry}
                  onOpen={onOpenGroundLoot}
                  onPin={pin}
                  title="Open the nearby treasure pack"
                />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <p className="combat-rewards-popup__hint">
        {pinned
          ? 'Pinned until closed.'
          : 'Closes in 5 seconds · hover or select to pin'}
      </p>
    </aside>
  );
}
